/**
 * @file WorkflowRunAPI.js
 * @description Funções para iniciar, melhorar e continuar a execução do workflow
 *
 * @exports default - Objeto contendo runWorkflow, userFeedback e generateMaterial
 */

/**
 * Processa stream SSE de qualquer ação do workflow (run, regenerate, continue)
 * @param {string} url - URL para a requisição da ação
 * @param {object} body - Corpo da requisição (inputs, action, stream=true)
 * @param {function} onProgress - Callback chamado para cada evento do stream
 * @param {object} options - Opções adicionais (incluindo signal para abort)
 * @returns {Promise<object>} - Resultado final do stream
 */

import { getAuthToken } from './TokenService'; 

const executeWorkflowAction = async (url, body, onProgress, options = {}) => {
    const token = await getAuthToken();
    let response;
    
    try {
        response = await fetch(url, {
            method: 'POST',
            headers: {
                'accept': 'application/json',
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
            signal: options.signal, 
        });
    } catch (err) {
        if (err.name === 'AbortError') {
            return null;
        }
        throw err;
    }
    
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        const error = new Error(`HTTP error, status: ${response.status}`);
        error.response = {
            status: response.status,
            data: errorData,
            detail: errorData.detail
        };
        
        throw error;
    }
        
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let lastData = null;
    
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop();
            
            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    try {
                        const dataContent = line.substring(6).trim();
                        if (!dataContent || dataContent === 'ping') {
                            continue;
                        }
                        
                        const jsonData = JSON.parse(dataContent);
                        lastData = jsonData;
                        
                        if (onProgress) {
                            onProgress(jsonData);
                        }
                    } catch (e) {
                        console.error('Erro ao processar JSON no stream:', e);
                    }
                }
            }
        }
    } catch (err) {
        if (err.name === 'AbortError') {
            return null;
        }
        throw err;
    }
    
    return lastData;
};

/**
 * Inicia a execução do workflow
 */
const runWorkflow = (workflowId, inputs, onProgress, options = {}) => {
    const url = `https://chat.int.bayer.com/api/v3/workflows/${workflowId}/runs`;
    
    const body = {
        inputs: inputs, 
        stream: true,
    };
    
    return executeWorkflowAction(url, body, onProgress, options);
};

/**
 * Regenera o conteúdo com feedback do usuário
 */
const userFeedback = (runId, userMessage, onProgress, options = {}) => {
    const urlFeedback = `https://chat.int.bayer.com/api/v3/workflows/runs/${runId}?action=regenerate`;
    
    const body = {
        action: "regenerate",
        user_message: userMessage,
        stream: true,
    };
    
    return executeWorkflowAction(urlFeedback, body, onProgress, options);
};

/**
 * Continua o workflow no node human feedback
 */
const generateMaterial = (runId, onProgress, options = {}) => {
    const urlFeedback = `https://chat.int.bayer.com/api/v3/workflows/runs/${runId}?action=continue`;
    
    const body = {
        action: "continue",
        stream: true,
    };
    
    return executeWorkflowAction(urlFeedback, body, onProgress, options);
};

const deleteWorkflowRun = async (runId) => {
    const token = await getAuthToken();
    const response = await fetch(`https://chat.int.bayer.com/api/v3/workflows/runs/${runId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json',
        },
    });

    if (response.status !== 204 && !response.ok) {
        throw new Error('Failed to delete workflow run');
    }
    return true;
};

export default {
    runWorkflow,
    userFeedback,
    generateMaterial,
    deleteWorkflowRun,
};